/**
 * Connection State Management
 *
 * Manages WebSocket connection lifecycle states.
 *
 * Ported from: connection.rs (64 lines)
 */

import { ConnectionState } from './types';

/**
 * Connection Manager
 *
 * Tracks and manages WebSocket connection state transitions.
 *
 * State Flow:
 * Closed → Connecting → Connected → Ready
 *         ↑_______________|
 *       (on disconnect/error)
 */
export class ConnectionManager {
  private state: ConnectionState = ConnectionState.Closed;
  private listeners: Set<(state: ConnectionState) => void> = new Set();

  /**
   * Get current connection state
   */
  getState(): ConnectionState {
    return this.state;
  }

  /**
   * Set connection state and notify listeners
   *
   * @param newState - New connection state
   */
  setState(newState: ConnectionState): void {
    if (this.state === newState) return;

    const oldState = this.state;
    this.state = newState;

    console.log(`🔄 Connection state: ${oldState} → ${newState}`);

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(newState);
      } catch (error) {
        console.error('Connection state listener error:', error);
      }
    });
  }

  /**
   * Check if connection is ready for requests
   */
  isReady(): boolean {
    return this.state === ConnectionState.Ready;
  }

  /**
   * Check if connection is established (Connected or Ready)
   */
  isConnected(): boolean {
    return this.state === ConnectionState.Connected || this.state === ConnectionState.Ready;
  }

  /**
   * Check if connection is closed
   */
  isClosed(): boolean {
    return this.state === ConnectionState.Closed;
  }

  /**
   * Check if connection is connecting
   */
  isConnecting(): boolean {
    return this.state === ConnectionState.Connecting;
  }

  /**
   * Subscribe to state changes
   *
   * @param listener - Callback function
   * @returns Unsubscribe function
   */
  onStateChange(listener: (state: ConnectionState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Reset to closed state
   */
  reset(): void {
    this.setState(ConnectionState.Closed);
  }
}
